import { Router, Request, Response } from 'express'
import { AuthCore } from '../core'

/**
 * Create Express OAuth routes
 */
export function createOAuthRoutes(auth: AuthCore): Router {
  const router = Router()

  /**
   * GET /oauth/authorize
   * Redirects to OAuth provider's authorization URL
   *
   * Query parameters:
   * - provider: OAuth provider ID (google, github, microsoft, discord)
   * - redirect_uri: Where to redirect after authentication
   * - code_challenge: Optional PKCE challenge (S256 method assumed)
   */
  router.get('/oauth/authorize', (req: Request, res: Response) => {
    try {
      const { provider, redirect_uri: redirectUri } = req.query

      if (!provider || typeof provider !== 'string') {
        return res.status(400).json({ error: 'Provider is required' })
      }

      if (!redirectUri || typeof redirectUri !== 'string') {
        return res.status(400).json({ error: 'Redirect URI is required' })
      }

      const { url, state, codeVerifier } = auth.getOAuthAuthorizationUrl(provider, redirectUri)

      // Store code verifier in session if using PKCE
      if (codeVerifier) {
        const session = (req as any).session || {}
        session.pkceVerifier = codeVerifier
        ;(req as any).session = session
      }

      res.redirect(url)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'OAuth authorization failed'
      res.status(400).json({ error: message })
    }
  })

  /**
   * GET /oauth/callback/:provider
   * OAuth provider redirects here after user authorization
   *
   * Query parameters:
   * - code: Authorization code
   * - state: State parameter for CSRF protection
   * - error: Error code if authorization failed
   */
  router.get('/oauth/callback/:provider', async (req: Request, res: Response) => {
    try {
      const { provider } = req.params
      const { code, state, error, error_description: errorDescription } = req.query

      // Handle authorization errors from provider
      if (error) {
        const description = typeof errorDescription === 'string'
          ? errorDescription
          : 'Authorization failed'
        return res.status(400).json({
          error: typeof error === 'string' ? error : 'authorization_error',
          error_description: description
        })
      }

      if (!code || typeof code !== 'string') {
        return res.status(400).json({ error: 'Authorization code is missing' })
      }

      if (!state || typeof state !== 'string') {
        return res.status(400).json({ error: 'State parameter is missing' })
      }

      // Get PKCE code verifier from session if available
      const codeVerifier = (req as any).session?.pkceVerifier as string | undefined

      // Handle OAuth callback
      const result = await auth.handleOAuthCallback(provider, code, state, codeVerifier)

      // Clear PKCE verifier from session
      if ((req as any).session?.pkceVerifier) {
        delete (req as any).session.pkceVerifier
      }

      // Return session and user info
      res.json({
        userId: result.userId,
        session: result.session,
        profile: result.profile,
        tokens: {
          accessToken: result.tokens.accessToken,
          refreshToken: result.tokens.refreshToken,
          expiresIn: result.tokens.expiresIn
        }
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'OAuth callback failed'
      res.status(400).json({ error: message })
    }
  })

  /**
   * POST /oauth/token/refresh
   * Refresh OAuth access token using refresh token
   *
   * Body:
   * - provider: OAuth provider ID
   * - refresh_token: Refresh token from previous authentication
   */
  router.post('/oauth/token/refresh', async (req: Request, res: Response) => {
    try {
      const { provider, refresh_token: refreshToken } = req.body

      if (!provider || typeof provider !== 'string') {
        return res.status(400).json({ error: 'Provider is required' })
      }

      if (!refreshToken || typeof refreshToken !== 'string') {
        return res.status(400).json({ error: 'Refresh token is required' })
      }

      const tokens = await auth.refreshOAuthToken(provider, refreshToken)

      res.json({
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken,
        expires_in: tokens.expiresIn,
        token_type: tokens.tokenType,
        scope: tokens.scope
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Token refresh failed'
      res.status(400).json({ error: message })
    }
  })

  /**
   * GET /oauth/providers
   * List available OAuth providers
   */
  router.get('/oauth/providers', (req: Request, res: Response) => {
    try {
      const providers = auth.listOAuthProviders().map(p => ({
        id: p.id,
        type: p.type,
        scopes: p.scopes || []
      }))

      res.json({ providers })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to list providers'
      res.status(400).json({ error: message })
    }
  })

  return router
}

/**
 * Type augmentation for Express session
 */
declare global {
  namespace Express {
    interface Session {
      pkceVerifier?: string
    }
  }
}
